import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AppError } from '@/core/errors/AppError';
import { EmpleadoFormSchema, } from '@/data/dto/empleado';
import { TIPOS_DOCUMENTO } from '@/data/dto/cliente';
import { empleadosApi } from '@/data/api/empleadosApi';
import { useCrearDisciplina, useCrearEmpleado, useCrearNivel, useDisciplinas, useEditarEmpleado, useNivelesDeDisciplina, } from '@/data/hooks/useEmpleados';
import { Button } from '@/shared/components/Button';
import { Field } from '@/shared/components/Field';
import { DatePicker } from '@/shared/components/DatePicker';
import { Input } from '@/shared/components/Input';
import { MoneyInput } from '@/shared/components/MoneyInput';
import { PhoneInput } from '@/shared/components/PhoneInput';
import { Select } from '@/shared/components/Select';
import { Textarea } from '@/shared/components/Textarea';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, } from '@/shared/components/Modal';
import { useToast } from '@/shared/components/Toast';
import { cn } from '@/core/utils/cn';
const FRECUENCIAS = ['DIARIA', 'SEMANAL', 'QUINCENAL', 'MENSUAL'];
const DEFAULT = {
    tipo: 'INSTRUCTOR',
    tipoDocumento: 'DNI',
    nroDocumento: '',
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    fechaAlta: new Date().toISOString().slice(0, 10),
    fechaBaja: '',
    cuil: '',
    frecuenciaLiquidacion: '',
    comisionSolicitadoPctOverride: '',
    comisionSolicitadoMontoOverride: '',
};
const NIVEL_DEFAULT = {
    disciplinaClave: '',
    nivelId: 0,
    fechaInicio: new Date().toISOString().slice(0, 10),
    observaciones: '',
};
export function EmpleadoFormDialog({ open, onOpenChange, empleado }) {
    const modoEdicion = Boolean(empleado);
    const toast = useToast();
    const crear = useCrearEmpleado();
    const editar = useEditarEmpleado();
    const isPending = crear.isPending || editar.isPending;
    const form = useForm({
        resolver: zodResolver(EmpleadoFormSchema),
        defaultValues: DEFAULT,
    });
    const [tab, setTab] = useState('datos');
    const [nivel, setNivel] = useState(NIVEL_DEFAULT);
    const [nivelError, setNivelError] = useState(null);
    useEffect(() => {
        if (!open)
            return;
        setTab('datos');
        setNivel(NIVEL_DEFAULT);
        setNivelError(null);
        form.reset(empleado
            ? {
                tipo: empleado.tipo ?? 'INSTRUCTOR',
                tipoDocumento: (empleado.tipoDocumento ?? 'DNI'),
                nroDocumento: empleado.nroDocumento,
                nombre: empleado.nombre,
                apellido: empleado.apellido,
                email: empleado.email ?? '',
                telefono: empleado.telefono ?? '',
                fechaAlta: empleado.fechaAlta,
                fechaBaja: empleado.fechaBaja ?? '',
                cuil: empleado.cuil ?? '',
                frecuenciaLiquidacion: empleado.frecuenciaLiquidacion ?? '',
                comisionSolicitadoPctOverride: empleado.comisionSolicitadoPctOverride != null
                    ? String(empleado.comisionSolicitadoPctOverride)
                    : '',
                comisionSolicitadoMontoOverride: empleado.comisionSolicitadoMontoOverride != null
                    ? String(empleado.comisionSolicitadoMontoOverride)
                    : '',
            }
            : DEFAULT);
    }, [open, empleado, form]);
    const onSubmit = form.handleSubmit(async (values) => {
        if (!modoEdicion && nivel.disciplinaClave && !nivel.nivelId) {
            setNivelError('Seleccioná un nivel o limpiá la disciplina.');
            setTab('nivel');
            return;
        }
        const onError = (err) => {
            if (err instanceof AppError && err.errores) {
                for (const e of err.errores) {
                    form.setError(e.campo, { message: e.mensaje });
                }
                setTab('datos');
                return;
            }
            toast.error(err instanceof AppError ? err.mensaje : 'No pudimos guardar.');
        };
        if (modoEdicion && empleado) {
            editar.mutate({ id: empleado.id, values }, {
                onSuccess: () => {
                    toast.exito('Instructor actualizado');
                    onOpenChange(false);
                },
                onError,
            });
            return;
        }
        crear.mutate(values, {
            onSuccess: async (creado) => {
                if (nivel.disciplinaClave && nivel.nivelId) {
                    try {
                        await empleadosApi.habilitar(creado.id, {
                            nivelId: nivel.nivelId,
                            fechaInicio: nivel.fechaInicio,
                            observaciones: nivel.observaciones,
                        });
                        toast.exito('Instructor creado con habilitación');
                    }
                    catch (err) {
                        toast.error(err instanceof AppError
                            ? `Instructor creado, pero no pudimos registrar la habilitación: ${err.mensaje}`
                            : 'Instructor creado, pero falló la habilitación.');
                    }
                }
                else {
                    toast.exito('Instructor creado');
                }
                onOpenChange(false);
            },
            onError,
        });
    });
    const errors = form.formState.errors;
    return (_jsx(Modal, { open: open, onOpenChange: onOpenChange, children: _jsxs(ModalContent, { size: "md", className: "flex max-h-[calc(100dvh-2rem)] flex-col", children: [_jsx(ModalHeader, { titulo: modoEdicion ? 'Editar instructor' : 'Nuevo instructor', descripcion: "Los campos con * son obligatorios." }), _jsxs("form", { onSubmit: onSubmit, noValidate: true, className: "flex min-h-0 flex-1 flex-col", children: [_jsx(ModalBody, { className: "min-h-0 flex-1 overflow-y-auto", children: modoEdicion ? (_jsx(DatosEmpleadoFields, { form: form, modoEdicion: modoEdicion, errors: errors })) : (_jsxs(Tabs.Root, { value: tab, onValueChange: (v) => setTab(v), children: [_jsxs(Tabs.List, { className: "mb-3 flex gap-1 border-b border-border-subtle", children: [_jsx(TabTrigger, { value: "datos", children: "Datos" }), _jsx(TabTrigger, { value: "nivel", children: "Nivel (opcional)" })] }), _jsx(Tabs.Content, { value: "datos", children: _jsx(DatosEmpleadoFields, { form: form, modoEdicion: modoEdicion, errors: errors }) }), _jsx(Tabs.Content, { value: "nivel", children: _jsx(NivelInicialFields, { value: nivel, onChange: (v) => {
                                                setNivel(v);
                                                setNivelError(null);
                                            }, error: nivelError }) })] })) }), _jsxs(ModalFooter, { children: [_jsx(Button, { type: "button", variant: "secondary", onClick: () => onOpenChange(false), disabled: isPending, children: "Cancelar" }), _jsx(Button, { type: "submit", cargando: isPending, children: modoEdicion ? 'Guardar cambios' : 'Crear instructor' })] })] })] }) }));
}
function TabTrigger({ value, children }) {
    return (_jsx(Tabs.Trigger, { value: value, className: cn('relative px-3 py-2 text-sm font-medium text-text-secondary transition', 'data-[state=active]:text-primary', 'after:absolute after:left-0 after:right-0 after:-bottom-px after:h-0.5 after:bg-transparent data-[state=active]:after:bg-primary'), children: children }));
}
function DatosEmpleadoFields({ form, modoEdicion, errors, }) {
    return (_jsxs("div", { className: "grid grid-cols-1 gap-3 sm:grid-cols-2", children: [_jsx(Field, { label: "Nombre", error: errors.nombre?.message, required: true, children: _jsx(Input, { autoFocus: true, ...form.register('nombre') }) }), _jsx(Field, { label: "Apellido", error: errors.apellido?.message, required: true, children: _jsx(Input, { ...form.register('apellido') }) }), _jsx(Field, { label: "Tipo documento", error: errors.tipoDocumento?.message, required: true, children: _jsx(Select, { ...form.register('tipoDocumento'), children: TIPOS_DOCUMENTO.map((t) => (_jsx("option", { value: t, children: t }, t))) }) }), _jsx(Field, { label: "N\u00BA documento", error: errors.nroDocumento?.message, required: true, children: _jsx(Input, { ...form.register('nroDocumento') }) }), _jsx(Field, { label: "Email", error: errors.email?.message, children: _jsx(Input, { type: "email", ...form.register('email') }) }), _jsx(Field, { label: "Tel\u00E9fono", error: errors.telefono?.message, children: _jsx(PhoneInput, { value: form.watch('telefono') ?? '', onChange: (v) => form.setValue('telefono', v, { shouldDirty: true }) }) }), _jsx(Field, { label: "CUIL", helper: "Formato XX-XXXXXXXX-X", error: errors.cuil?.message, children: _jsx(Input, { placeholder: "20-12345678-3", ...form.register('cuil') }) }), _jsx(Field, { label: "Fecha de alta", error: errors.fechaAlta?.message, required: true, children: _jsx(DatePicker, { value: form.watch('fechaAlta') ?? '', onChange: (v) => form.setValue('fechaAlta', v, { shouldDirty: true }) }) }), _jsx(Field, { label: "Fecha de baja", helper: modoEdicion ? 'Dejar vacío si sigue activo' : 'Solo disponible al editar', error: errors.fechaBaja?.message, children: _jsx(DatePicker, { disabled: !modoEdicion, value: form.watch('fechaBaja') ?? '', onChange: (v) => form.setValue('fechaBaja', v, { shouldDirty: true }) }) }), _jsx(Field, { label: "Frecuencia de liquidaci\u00F3n", helper: "Vac\u00EDo = usa la de la escuela", error: errors.frecuenciaLiquidacion?.message, children: _jsxs(Select, { ...form.register('frecuenciaLiquidacion'), children: [_jsx("option", { value: "", children: "Por defecto" }), FRECUENCIAS.map((f) => (_jsx("option", { value: f, children: f }, f)))] }) }), _jsx(Field, { label: "Override comisi\u00F3n requerido (%)", helper: "Si el cliente lo requiere y no se define, usa el par\u00E1metro general", children: _jsx(Input, { inputMode: "decimal", placeholder: "30", ...form.register('comisionSolicitadoPctOverride') }) }), _jsx(Field, { label: "Override comisi\u00F3n requerido (monto)", children: _jsx(MoneyInput, { value: form.watch('comisionSolicitadoMontoOverride') ?? '', onChange: (v) => form.setValue('comisionSolicitadoMontoOverride', v, { shouldDirty: true }) }) })] }));
}
function NivelInicialFields({ value, onChange, error, }) {
    const toast = useToast();
    const { data: disciplinas } = useDisciplinas();
    const { data: niveles } = useNivelesDeDisciplina(value.disciplinaClave || null);
    const crearDisciplina = useCrearDisciplina();
    const crearNivel = useCrearNivel();
    const [creandoDisciplina, setCreandoDisciplina] = useState(false);
    const [nuevaDisciplina, setNuevaDisciplina] = useState('');
    const [creandoNivel, setCreandoNivel] = useState(false);
    const [nuevoNivel, setNuevoNivel] = useState('');
    const ejecutarCrearDisciplina = () => {
        const nombre = nuevaDisciplina.trim();
        if (!nombre)
            return;
        crearDisciplina.mutate({ nombre }, {
            onSuccess: (d) => {
                toast.exito('Disciplina creada');
                onChange({ ...value, disciplinaClave: d.clave, nivelId: 0 });
                setCreandoDisciplina(false);
                setNuevaDisciplina('');
            },
            onError: (err) => toast.error(err instanceof AppError ? err.mensaje : 'No pudimos crear la disciplina.'),
        });
    };
    const ejecutarCrearNivel = () => {
        const nombre = nuevoNivel.trim();
        if (!nombre || !value.disciplinaClave)
            return;
        crearNivel.mutate({ disciplinaClave: value.disciplinaClave, nombre }, {
            onSuccess: (n) => {
                toast.exito('Nivel creado');
                onChange({ ...value, nivelId: n.id ?? 0 });
                setCreandoNivel(false);
                setNuevoNivel('');
            },
            onError: (err) => toast.error(err instanceof AppError ? err.mensaje : 'No pudimos crear el nivel.'),
        });
    };
    return (_jsxs("div", { className: "flex flex-col gap-3", children: [_jsx("p", { className: "text-xs text-text-tertiary", children: "Asoci\u00E1 una disciplina y nivel iniciales al instructor. Es opcional: si lo dej\u00E1s vac\u00EDo, pod\u00E9s agregarlo despu\u00E9s desde el detalle." }), _jsxs("div", { className: "grid grid-cols-1 gap-3 sm:grid-cols-2", children: [_jsx(Field, { label: "Disciplina", acciones: !creandoDisciplina ? (_jsx("button", { type: "button", onClick: () => setCreandoDisciplina(true), className: "text-[11px] font-medium text-primary hover:underline", children: "+ Nueva" })) : null, children: creandoDisciplina ? (_jsxs("div", { className: "flex items-center gap-1.5", children: [_jsx(Input, { autoFocus: true, value: nuevaDisciplina, onChange: (e) => setNuevaDisciplina(e.target.value), placeholder: "Nombre de la disciplina", onKeyDown: (e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            ejecutarCrearDisciplina();
                                        }
                                    } }), _jsx(Button, { type: "button", size: "sm", onClick: ejecutarCrearDisciplina, cargando: crearDisciplina.isPending, children: "Crear" }), _jsx(Button, { type: "button", variant: "ghost", size: "sm", onClick: () => {
                                        setCreandoDisciplina(false);
                                        setNuevaDisciplina('');
                                    }, children: "Cancelar" })] })) : (_jsxs(Select, { value: value.disciplinaClave, onChange: (e) => onChange({ ...value, disciplinaClave: e.target.value, nivelId: 0 }), children: [_jsx("option", { value: "", children: "Sin asignar" }), disciplinas?.map((d) => (_jsx("option", { value: d.clave, children: d.nombre ?? d.clave }, d.clave)))] })) }), _jsx(Field, { label: "Nivel", error: error ?? undefined, acciones: !creandoNivel && value.disciplinaClave ? (_jsx("button", { type: "button", onClick: () => setCreandoNivel(true), className: "text-[11px] font-medium text-primary hover:underline", children: "+ Nuevo" })) : null, children: creandoNivel ? (_jsxs("div", { className: "flex items-center gap-1.5", children: [_jsx(Input, { autoFocus: true, value: nuevoNivel, onChange: (e) => setNuevoNivel(e.target.value), placeholder: "Nombre del nivel", onKeyDown: (e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            ejecutarCrearNivel();
                                        }
                                    } }), _jsx(Button, { type: "button", size: "sm", onClick: ejecutarCrearNivel, cargando: crearNivel.isPending, children: "Crear" }), _jsx(Button, { type: "button", variant: "ghost", size: "sm", onClick: () => {
                                        setCreandoNivel(false);
                                        setNuevoNivel('');
                                    }, children: "Cancelar" })] })) : (_jsxs(Select, { value: String(value.nivelId || ''), disabled: !value.disciplinaClave, onChange: (e) => onChange({ ...value, nivelId: Number(e.target.value) || 0 }), children: [_jsx("option", { value: "", children: "Seleccion\u00E1\u2026" }), niveles?.map((n) => (_jsx("option", { value: n.id, children: n.nombre }, n.id)))] })) }), _jsx(Field, { label: "Fecha de inicio", children: _jsx(Input, { type: "date", value: value.fechaInicio, onChange: (e) => onChange({ ...value, fechaInicio: e.target.value }), disabled: !value.disciplinaClave }) }), _jsx(Field, { label: "Observaciones", className: "sm:col-span-2", children: _jsx(Textarea, { rows: 2, value: value.observaciones, onChange: (e) => onChange({ ...value, observaciones: e.target.value }), disabled: !value.disciplinaClave }) })] })] }));
}
